import { historyMessages, reportMessages, storageMessages } from "@studystraight/shared-config";
import type { SessionRepository } from "@studystraight/session-core";
import type { StudySession } from "@studystraight/shared-types";
import { useCallback, useEffect, useState } from "react";

import { formatDay, formatMinutes, formatTime } from "../../app/format.js";

export interface HistoryScreenProps {
  /** `null` trong lúc database đang mở. */
  repository: SessionRepository | null;
  persistent: boolean;
  onOpen: (session: StudySession) => void;
  onBack: () => void;
}

/** §27 — lịch sử theo ngày, mở lại report cũ, xoá toàn bộ. */
export function HistoryScreen({
  repository,
  persistent,
  onOpen,
  onBack,
}: HistoryScreenProps): React.JSX.Element {
  const [sessions, setSessions] = useState<readonly StudySession[] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (repository === null) return;
    let disposed = false;
    void repository.getAll().then((all) => {
      if (!disposed) setSessions(all);
    });
    return () => {
      disposed = true;
    };
  }, [repository]);

  const deleteAll = useCallback(() => {
    if (repository === null) return;
    void repository.deleteAll().then(() => {
      setSessions([]);
      setConfirming(false);
      setDeleted(true);
    });
  }, [repository]);

  let lastDay = "";

  return (
    <main className="screen">
      <header className="topbar">
        <button type="button" className="btn btn--ghost" onClick={onBack}>
          ← Quay lại
        </button>
        <h2 className="topbar__title">{historyMessages.title}</h2>
      </header>

      <div className="history">
        {persistent ? null : <p className="hint">{storageMessages.unavailable}</p>}
        {deleted ? <p className="hint">{historyMessages.deleted}</p> : null}

        {sessions === null ? (
          <p className="hint">{historyMessages.loading}</p>
        ) : sessions.length === 0 ? (
          <p className="hint">{historyMessages.empty}</p>
        ) : (
          <ul className="history__list">
            {sessions.map((session) => {
              // `getAll()` đã sắp mới nhất trước, nên chỉ cần so với phần tử liền trước để biết
              // khi nào sang ngày mới — không phải group thành object rồi sort lại.
              const day = formatDay(session.startTime);
              const newDay = day !== lastDay;
              lastDay = day;
              return (
                <li key={session.id}>
                  {newDay ? <h3 className="history__day">{day}</h3> : null}
                  <button
                    type="button"
                    className="card history__item"
                    onClick={() => onOpen(session)}
                  >
                    <span className="card__title">
                      {formatTime(session.startTime)} · {formatMinutes(session.duration)}
                    </span>
                    <span className="card__hint">
                      {reportMessages.goodPosture} {session.goodPosturePercentage}% ·{" "}
                      {reportMessages.averageScore} {session.averagePostureScore} ·{" "}
                      {reportMessages.alerts} {session.alertCount}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {/* §7 — đường xoá dữ liệu phải luôn có và phải hỏi lại một lần (không dùng `confirm()`:
            trên iOS nó chặn cả tab và trông như app bị treo). */}
        {confirming ? (
          <div className="banner" role="alertdialog" aria-label={storageMessages.deleteAllConfirm}>
            <span>{storageMessages.deleteAllConfirm}</span>
            <div className="actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setConfirming(false)}
              >
                Giữ lại
              </button>
              <button type="button" className="btn btn--danger" onClick={deleteAll}>
                Xoá
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={repository === null || sessions?.length === 0}
            onClick={() => setConfirming(true)}
          >
            {historyMessages.deleteAll}
          </button>
        )}
      </div>
    </main>
  );
}
